import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState, useEffect } from "react";
import {
  ArrowRight,
  Plus,
  Upload,
  AlertCircle,
  X,
  Info,
  Printer,
  Layers,
  Sun,
  ShoppingBag,
  Star,
} from "lucide-react";
import { useDemoMode } from "@/hooks/use-demo-mode";
import { useAuth } from "@/hooks/use-auth";
import { getDataSource } from "@/lib/data-source";
import { getIntegration } from "@/lib/integrations";
import { supabase } from "@/integrations/supabase/client";
import type { Product } from "@/lib/products";
import { StatusChip } from "@/components/StatusChip";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { FavouriteButton } from "@/components/FavouriteButton";
import { listFavouriteJobIds } from "@/lib/favourites";

export const Route = createFileRoute("/app/customer/dashboard")({ component: Dashboard });

function Dashboard() {
  const { demoMode } = useDemoMode();
  const { profile } = useAuth();
  const ds = useMemo(
    () => getDataSource(profile?.role === "staff" && demoMode),
    [demoMode, profile?.role],
  );

  const [announcement, setAnnouncement] = useState<string | null>(null);
  const [showAnnouncement, setShowAnnouncement] = useState(false);

  useEffect(() => {
    const loadAnnouncement = async () => {
      const enabled = await getIntegration("customer_dashboard_announcement_enabled");
      if (enabled === "true") {
        const content = await getIntegration("customer_dashboard_announcement");
        if (content) {
          const dismissed = localStorage.getItem("fd-dashboard-announcement-v1");
          if (!dismissed) {
            setAnnouncement(content);
            setShowAnnouncement(true);
          }
        }
      }
    };
    loadAnnouncement();
  }, []);

  const handleDismissAnnouncement = () => {
    setShowAnnouncement(false);
    localStorage.setItem("fd-dashboard-announcement-v1", "true");
  };

  const {
    data: jobs = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["customer-jobs", demoMode, profile?.customer_id],
    queryFn: () =>
      ds.listJobs(profile?.customer_id ? { customer_id: profile.customer_id } : undefined),
  });

  const active = jobs.filter((j) => j.status !== "Completed" && j.status !== "On Hold");
  const completed = jobs.filter((j) => j.status === "Completed");

  const { data: favIds = [] } = useQuery({
    queryKey: ["favourites", profile?.id],
    queryFn: () => (profile?.id ? listFavouriteJobIds(profile.id) : Promise.resolve([])),
    enabled: !!profile?.id,
  });
  const favouriteJobs = useMemo(() => jobs.filter((j) => favIds.includes(j.id)), [jobs, favIds]);

  const { data: products = [] } = useQuery({
    queryKey: ["products-public"],
    queryFn: async () => {
      const { data, error } = await supabase.from("products").select("*").eq("active", true);
      if (error) throw error;
      return data ?? [];
    },
  });

  return (
    <div className="space-y-8">
      {/* Announcement Banner */}
      {showAnnouncement && announcement && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.2 }}
          className="rounded-3xl border border-info/20 bg-info/10 p-4 flex items-start gap-3"
        >
          <Info className="h-5 w-5 text-info mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-foreground">{announcement}</p>
          </div>
          <button
            onClick={handleDismissAnnouncement}
            className="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-[2.5rem] bg-gradient-hero p-10 text-primary-foreground shadow-pop md:p-14"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -right-16 -top-16 h-72 w-72 rounded-full bg-[oklch(0.88_0.16_95/0.35)] blur-2xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-24 -left-12 h-64 w-64 rounded-full bg-[oklch(0.7_0.22_350/0.35)] blur-2xl"
        />
        <div className="relative flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="max-w-2xl">
            <h1 className="text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
              Your DTF jobs, all in one place
            </h1>
            <p className="mt-3 text-lg text-primary-foreground/90 md:text-xl">
              Create jobs, upload artwork, and track progress — everything you need to keep your
              printing moving.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/app/customer/shop">
              <Button
                size="lg"
                className="rounded-full bg-card px-8 text-base font-semibold text-foreground shadow-pop hover:bg-card/90"
              >
                <ShoppingBag className="mr-2 h-5 w-5" /> Go to Shop
              </Button>
            </Link>
          </div>
        </div>
      </motion.div>

      {/* Start with shortcuts */}
      {products.length > 0 && (
        <section>
          <h2 className="text-xl font-bold tracking-tight mb-4">Start with</h2>
          <div className="grid gap-4 md:grid-cols-3">
            {products.slice(0, 3).map((product: Product) => (
              <Link
                key={product.id}
                to="/app/customer/shop/$productId"
                params={{ productId: product.id }}
                className="group rounded-3xl border border-border bg-card p-5 shadow-soft transition-all hover:-translate-y-0.5 hover:shadow-pop"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="grid h-10 w-10 place-items-center rounded-2xl bg-gradient-soft text-primary">
                    <Printer className="h-5 w-5" />
                  </div>
                  <div className="font-semibold">{product.name}</div>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  {product.description || "Start a new print job"}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">from £{product.price_per_m}/m</span>
                  <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section>
        <div className="mb-4 flex items-baseline justify-between">
          <h2 className="text-2xl font-bold tracking-tight">Active jobs</h2>
          <span className="rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-secondary-foreground">
            {active.length}
          </span>
        </div>
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            Loading your jobs…
          </div>
        ) : error ? (
          <div className="rounded-3xl border border-amber-200 bg-amber-50 p-6 text-amber-800 dark:border-amber-800 dark:bg-amber-950/20 dark:text-amber-200">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 mt-0.5" />
              <div>
                <p className="font-medium">We couldn't load your jobs</p>
                <p className="mt-1 text-sm">Please refresh or contact support if this continues.</p>
              </div>
            </div>
          </div>
        ) : active.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {active.map((j) => {
              const isOverdue = j.due_date && new Date(j.due_date) < new Date();
              return (
                <div key={j.id} className="group relative">
                  <Link to="/app/customer/jobs/$id" params={{ id: j.id }} className="block">
                    <div className="rounded-3xl border border-border bg-card p-5 shadow-soft transition-all group-hover:-translate-y-0.5 group-hover:shadow-pop">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="font-semibold">{j.job_title}</div>
                          <div className="text-xs text-muted-foreground">
                            {j.product_type} · {j.print_size} · Qty {j.quantity}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2 pr-8">
                          <StatusChip status={j.status} />
                          {isOverdue && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                              <AlertCircle className="h-3 w-3" /> Past due
                            </span>
                          )}
                        </div>
                      </div>
                      <ProgressBar status={j.status} />
                      <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                        <span>
                          {j.due_date
                            ? `Due ${format(new Date(j.due_date), "d MMM")}`
                            : "No due date"}
                        </span>
                        <span className="inline-flex items-center gap-1 text-primary opacity-0 transition-opacity group-hover:opacity-100">
                          View job <ArrowRight className="h-3 w-3" />
                        </span>
                      </div>
                    </div>
                  </Link>
                  <div className="absolute right-3 top-3">
                    <FavouriteButton jobId={j.id} size="sm" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {favouriteJobs.length > 0 && (
        <section>
          <h2 className="mb-3 flex items-center gap-2 text-xl font-bold">
            <Star className="h-5 w-5 fill-current text-amber-500" /> Favourites
          </h2>
          <div className="grid gap-3 md:grid-cols-3">
            {favouriteJobs.map((j) => (
              <Link
                key={j.id}
                to="/app/customer/jobs/$id"
                params={{ id: j.id }}
                className="rounded-2xl border border-border bg-card p-4 hover:bg-secondary"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="text-sm font-semibold">{j.job_title}</div>
                  <StatusChip status={j.status} />
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {j.product_type} · Qty {j.quantity}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {completed.length > 0 && (
        <section>
          <h2 className="mb-3 text-xl font-bold">Recently completed</h2>
          <div className="grid gap-3 md:grid-cols-3">
            {completed.slice(0, 6).map((j) => (
              <Link
                key={j.id}
                to="/app/customer/jobs/$id"
                params={{ id: j.id }}
                className="rounded-2xl border border-border bg-card p-4 hover:bg-secondary"
              >
                <div className="text-sm font-semibold">{j.job_title}</div>
                <div className="mt-1 text-xs text-muted-foreground">Qty {j.quantity}</div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-3xl border border-dashed border-border bg-card/50 p-10 text-center">
      <p className="text-lg text-muted-foreground">No jobs yet.</p>
      <p className="mt-2 text-sm text-muted-foreground">
        When you submit your first job, it will appear here with live status updates.
      </p>
      <Link to="/app/customer/shop">
        <Button className="mt-4 rounded-full bg-gradient-hero text-primary-foreground">
          <ShoppingBag className="mr-1.5 h-4 w-4" /> Browse products
        </Button>
      </Link>
    </div>
  );
}

const STATUS_PROGRESS: Record<string, number> = {
  New: 10,
  "Artwork Check": 25,
  "Gang Sheet": 40,
  "Queued for Print": 55,
  Printing: 70,
  Curing: 80,
  "Quality Check": 88,
  "Ready for Dispatch": 95,
  Completed: 100,
  "On Hold": 0,
};
function ProgressBar({ status }: { status: string }) {
  const pct = STATUS_PROGRESS[status] ?? 0;
  return (
    <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-secondary">
      <div
        className="h-full rounded-full bg-gradient-hero transition-all"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}
